<script setup lang="ts" generic="T">
/**
 * „Sortierbare Liste“: a list whose entries are reordered with a grip — by
 * pointer (drag) or keyboard (Space lifts, arrows move, Space drops, Escape
 * cancels, Alt+arrow moves one step). Wraps `useNxSortable`; the caller keeps
 * the array (`v-model:items`) or listens to `reorder`.
 */
import GripVertical from 'lucide-vue-next/dist/esm/icons/grip-vertical.js';
import { computed, useTemplateRef } from 'vue';
import { reorderItems } from './sortMath';
import { useNxSortable, type NxSortableAxis, type NxSortableMessages } from './useNxSortable';

const props = withDefaults(
  defineProps<{
    items: readonly T[];
    /** Item identity: a field name or a function. Default `'id'`, falling back to the index. */
    itemKey?: string | ((item: T) => string | number);
    axis?: NxSortableAxis;
    disabled?: boolean;
    /** Spoken name of an item (grip label, announcements). */
    labelOf?: (item: T, index: number) => string;
    /** Accessible name of the list. */
    ariaLabel?: string;
    /** Render the built-in grip. `false` to place `handleProps` on your own grip in the slot. */
    grip?: boolean;
    messages?: Partial<NxSortableMessages>;
  }>(),
  {
    itemKey: 'id',
    axis: 'vertical',
    disabled: false,
    labelOf: undefined,
    ariaLabel: undefined,
    grip: true,
    messages: undefined,
  }
);

const emit = defineEmits<{
  /** The item at `from` should move to `to`. */
  reorder: [from: number, to: number];
  /** The reordered array — for `v-model:items`. */
  'update:items': [items: T[]];
}>();

defineSlots<{
  item(props: {
    item: T;
    index: number;
    handleProps: Record<string, unknown>;
    dragging: boolean;
    dropSide: 'before' | 'after' | null;
  }): unknown;
}>();

const listEl = useTemplateRef<HTMLElement>('list');

const sortable = useNxSortable<T>({
  items: () => props.items,
  container: listEl,
  axis: props.axis,
  disabled: () => props.disabled,
  labelOf: (item, index) => props.labelOf?.(item, index) ?? `Eintrag ${index + 1}`,
  messages: props.messages,
  onReorder: (from, to) => {
    emit('reorder', from, to);
    emit('update:items', reorderItems(props.items, from, to));
  },
});

function keyOf(item: T, index: number): string | number {
  const key = props.itemKey;
  if (typeof key === 'function') return key(item);
  const value =
    item !== null && typeof item === 'object' ? (item as Record<string, unknown>)[key] : undefined;
  return typeof value === 'string' || typeof value === 'number' ? value : index;
}

const itemText = (item: T, index: number) =>
  props.labelOf?.(item, index) ??
  (typeof item === 'object' ? `Eintrag ${index + 1}` : String(item));

const isMoving = computed(() => sortable.active.value);

defineExpose({ cancel: sortable.cancel });
</script>

<template>
  <ul
    ref="list"
    class="nx-sortable-list"
    :class="[`is-${axis}`, { 'is-moving': isMoving, 'is-disabled': disabled }]"
    :aria-label="ariaLabel"
  >
    <li
      v-for="(item, index) in items"
      :key="keyOf(item, index)"
      class="nx-sortable-list__item"
      v-bind="sortable.itemProps(index)"
    >
      <button
        v-if="grip"
        type="button"
        class="nx-sortable-list__grip"
        :disabled="disabled"
        v-bind="sortable.handleProps(index)"
      >
        <GripVertical aria-hidden="true" />
      </button>
      <div class="nx-sortable-list__content">
        <slot
          name="item"
          :item="item"
          :index="index"
          :handle-props="sortable.handleProps(index)"
          :dragging="sortable.isDragging(index)"
          :drop-side="sortable.dropSide(index)"
        >
          {{ itemText(item, index) }}
        </slot>
      </div>
    </li>
  </ul>
</template>

<style scoped>
.nx-sortable-list {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}

.nx-sortable-list.is-horizontal {
  flex-direction: row;
  flex-wrap: nowrap;
}

.nx-sortable-list__item {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm) var(--nxt-space-xs) var(--nxt-space-2xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  transition:
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out),
    opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}

/* The dragged item follows the pointer, lifted above its neighbours. */
.nx-sortable-list__item[data-nx-sortable-state='pointer'] {
  box-shadow: var(--nxt-shadow-lg);
  border-color: color-mix(in srgb, var(--nxt-brand-bg) 45%, var(--nxt-border));
  cursor: grabbing;
  transition: box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);
}

/* Keyboard-lifted: stays put, a dashed brand frame says „in the hand“. */
.nx-sortable-list__item[data-nx-sortable-state='keyboard'] {
  outline: calc(var(--nxt-hairline) * 2) dashed var(--nxt-brand-bg);
  outline-offset: calc(var(--nxt-hairline) * 2);
  background: color-mix(in srgb, var(--nxt-brand-bg) 6%, var(--nxt-surface-bg));
}

/* Landing-slot indicator: a brand bar in the gap before or after the target. */
.nx-sortable-list__item[data-nx-drop-side]::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  block-size: calc(var(--nxt-hairline) * 3);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}

.nx-sortable-list__item[data-nx-drop-side='before']::after {
  top: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline) * 2);
}

.nx-sortable-list__item[data-nx-drop-side='after']::after {
  bottom: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline) * 2);
}

.is-horizontal > .nx-sortable-list__item[data-nx-drop-side]::after {
  inset-inline: auto;
  inset-block: 0;
  block-size: auto;
  inline-size: calc(var(--nxt-hairline) * 3);
}

.is-horizontal > .nx-sortable-list__item[data-nx-drop-side='before']::after {
  top: 0;
  left: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline) * 2);
}

.is-horizontal > .nx-sortable-list__item[data-nx-drop-side='after']::after {
  bottom: 0;
  right: calc(var(--nxt-space-xs) / -2 - var(--nxt-hairline) * 2);
}

.nx-sortable-list__grip {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
  inline-size: var(--nxt-icon-size-xl);
  block-size: var(--nxt-icon-size-xl);
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: transparent;
  color: var(--nxt-fg-subtle);
  cursor: grab;
}

.nx-sortable-list__grip > svg {
  inline-size: var(--nxt-icon-size-md);
  block-size: var(--nxt-icon-size-md);
}

.nx-sortable-list__grip:hover {
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
}

.nx-sortable-list__grip:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

.nx-sortable-list__grip[aria-pressed='true'] {
  color: var(--nxt-brand-bg);
  cursor: grabbing;
}

.nx-sortable-list__grip:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.nx-sortable-list__content {
  flex: 1 1 auto;
  min-width: 0;
}

@media (prefers-reduced-motion: reduce) {
  .nx-sortable-list__item,
  .nx-sortable-list__item[data-nx-sortable-state='pointer'] {
    transition: none;
  }
}
</style>
